/**
 * Utility functions for file type detection and icon selection
 */

export type FileTypeIcon = 'csv-doc' | 'xlsx' | 'databricks' | 'generic';

/**
 * Determines the appropriate icon type based on file name and file type
 * @param fileName - The name of the file
 * @param fileType - The type/extension of the file
 * @returns The icon type to use for display
 */
export function getFileTypeIcon(fileName: string = '', fileType: string = ''): FileTypeIcon {
    const lowerFileName = fileName.toLowerCase();
    const lowerFileType = fileType.toLowerCase();

    // Check for CSV files - use custom csv-doc icon
    if (lowerFileType === 'csv' || lowerFileName.endsWith('.csv')) {
        return 'csv-doc';
    }

    // Check for Excel files (various formats)
    if (
        lowerFileType.includes('xlsx') ||
        lowerFileType.includes('excel') ||
        lowerFileType.includes('xls') ||
        lowerFileName.endsWith('.xlsx') ||
        lowerFileName.endsWith('.xls')
    ) {
        return 'xlsx';
    }

    // Check for Databricks files (by name, type, or .delta extension)
    if (
        lowerFileName.includes('databricks') ||
        lowerFileType.includes('databricks') ||
        lowerFileName.includes('dbfs') ||
        lowerFileType.includes('dbfs') ||
        lowerFileName.endsWith('.delta') ||
        lowerFileType === 'databricks'
    ) {
        return 'databricks';
    }

    // Default to generic file icon
    return 'generic';
}

/**
 * Gets the inline SVG content for a given file type
 * @param iconType - The type of icon needed
 * @returns The SVG content as a string, or null if should use external file
 */
export function getFileIconSvgContent(iconType: FileTypeIcon): string | null {
    switch (iconType) {
        case 'csv-doc':
            return `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
  <path fill="#fff" d="M13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39c0 .275.225.5.5.5z"/>
  <path fill="#fff" d="M42.293 13 34 4.707V12.5c0 .275.225.5.5.5h7.793z"/>
  <path fill="#605E5C" fill-rule="evenodd" d="m43.56 12.854-9.414-9.415A1.51 1.51 0 0 0 33.086 3H13.5c-.827 0-1.5.673-1.5 1.5v39c0 .827.673 1.5 1.5 1.5h29c.827 0 1.5-.673 1.5-1.5V13.914c0-.4-.156-.777-.44-1.06zM34 4.707 42.293 13H34.5a.501.501 0 0 1-.5-.5V4.707zM13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39a.5.5 0 0 0 .5.5z" clip-rule="evenodd" opacity=".64"/>
  <path fill="#22c55e" d="M6 37h18a2 2 0 0 0 2-2V17a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2z"/>
  <text x="15" y="30" font-family="Arial" font-size="9" font-weight="bold" fill="white" text-anchor="middle">CSV</text>
</svg>`;
        case 'xlsx':
            return `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
  <path fill="#fff" d="M13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39c0 .275.225.5.5.5z"/>
  <path fill="#fff" d="M42.293 13 34 4.707V12.5c0 .275.225.5.5.5h7.793z"/>
  <path fill="#605E5C" fill-rule="evenodd" d="m43.56 12.854-9.414-9.415A1.51 1.51 0 0 0 33.086 3H13.5c-.827 0-1.5.673-1.5 1.5v39c0 .827.673 1.5 1.5 1.5h29c.827 0 1.5-.673 1.5-1.5V13.914c0-.4-.156-.777-.44-1.06zM34 4.707 42.293 13H34.5a.501.501 0 0 1-.5-.5V4.707zM13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39a.5.5 0 0 0 .5.5z" clip-rule="evenodd" opacity=".64"/>
  <path fill="#134A2C" d="M39 31h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#185C37" d="M32 31h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#21A366" d="M39 27h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#107C41" d="M32 27h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#33C481" d="M39 23h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#21A366" d="M32 23h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2z"/>
  <path fill="#107C41" d="M6 37h18a2 2 0 0 0 2-2V17a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2z"/>
  <path fill="#F9F7F7" d="m9.519 32 3.85-6.017L9.843 20h2.839l1.926 3.825c.177.362.298.633.365.811h.025c.127-.29.26-.572.398-.845L17.455 20h2.605l-3.62 5.95L20.15 32h-2.77l-2.225-4.2a3.514 3.514 0 0 1-.265-.561h-.033a2.61 2.61 0 0 1-.257.543L12.308 32h-2.79z"/>
</svg>`;
        case 'databricks':
            return `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Databricks file">
    <path fill="#fff" d="M13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39c0 .275.225.5.5.5z"/>
    <path fill="#fff" d="M42.293 13 34 4.707V12.5c0 .275.225.5.5.5h7.793z"/>
    <path fill="#605E5C" fill-rule="evenodd" d="m43.56 12.854-9.414-9.415A1.51 1.51 0 0 0 33.086 3H13.5c-.827 0-1.5.673-1.5 1.5v39c0 .827.673 1.5 1.5 1.5h29c.827 0 1.5-.673 1.5-1.5V13.914c0-.4-.156-.777-.44-1.06zM34 4.707 42.293 13H34.5a.501.501 0 0 1-.5-.5V4.707zM13.5 44h29c.275 0 .5-.225.5-.5V14h-8.5c-.827 0-1.5-.673-1.5-1.5V4H13.5c-.275 0-.5.225-.5.5v39a.5.5 0 0 0 .5.5z" clip-rule="evenodd" opacity=".5"/>
    <rect x="6" y="17" width="18" height="20" rx="2" fill="#FFFFFF" stroke="#FF3621" stroke-width="1"/>
    <g transform="translate(-9,19) scale(0.5)">
        <path fill="#FF3621" d="M59.7,12.5l-12.5,7.1l-13.3-7.6l-0.6,0.3v5.5l14,7.9l12.5-7.1v2.9l-12.5,7.1l-13.3-7.6l-0.6,0.3v0.9l14,7.9l14-7.9V17l-0.6-0.3l-13.4,7.6l-12.5-7.1v-2.9l12.5,7.1l14-7.9V7.9l-0.7-0.4l-13.3,7.5L35.4,8.3l11.8-6.7L57,7.1l0.9-0.5V6L47.2,0l-14,7.9v0.8l14,7.9l12.5-7.1V12.5z"/>
    </g>
</svg>`;
        case 'generic':
        default:
            return null; // Use external file
    }
}

/**
 * Gets the icon path for a given file type
 * @param iconType - The type of icon needed
 * @returns The path to the icon file
 */
export function getFileIconPath(iconType: FileTypeIcon): string {
    switch (iconType) {
        case 'csv-doc':
        case 'xlsx':
        case 'databricks':
        case 'generic':
        default:
            return ''; // All icons use inline SVG now
    }
}

/**
 * Gets the alt text for a given file type icon
 * @param iconType - The type of icon
 * @returns Human-readable alt text for accessibility
 */
export function getFileIconAltText(iconType: FileTypeIcon): string {
    switch (iconType) {
        case 'csv-doc':
            return 'CSV file';
        case 'xlsx':
            return 'Excel file';
        case 'databricks':
            return 'Databricks file';
        case 'generic':
        default:
            return 'File';
    }
}